// v10/src/components/wb/WBResultPanel.js
//
// Panel de resultado del cálculo W&B.
//
// Props:
//   - result: { total_weight_lb, total_moment_lb_in, cg_in, mtow_lb,
//               fuel_used_gal, fuel_used_lb, within_envelope, envelope_status,
//               limits, breakdown }
//   - darkMode

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';

const WBResultPanel = ({ result, darkMode }) => {
  const { t } = useTranslation();

  const text = darkMode ? 'white' : '#0f172a';
  const sub = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';

  if (!result) return null;

  const statusConfig = {
    within: {
      color: '#10b981',
      bg: 'rgba(16,185,129,0.12)',
      icon: CheckCircle2,
      label: t('wb_status_within', { ns: 'admin' }),
    },
    over_mtow: {
      color: '#ef4444',
      bg: 'rgba(239,68,68,0.12)',
      icon: AlertTriangle,
      label: t('wb_status_over_mtow', { ns: 'admin' }),
    },
    cg_too_forward: {
      color: '#ef4444',
      bg: 'rgba(239,68,68,0.12)',
      icon: AlertCircle,
      label: t('wb_status_cg_forward', { ns: 'admin' }),
    },
    cg_too_aft: {
      color: '#ef4444',
      bg: 'rgba(239,68,68,0.12)',
      icon: AlertCircle,
      label: t('wb_status_cg_aft', { ns: 'admin' }),
    },
  };

  const status = statusConfig[result.envelope_status] || statusConfig.within;
  const StatusIcon = status.icon;

  const overMtow = result.mtow_lb > 0 && result.total_weight_lb > result.mtow_lb;

  return (
    <div
      style={{
        background: cardBg,
        border,
        borderRadius: 18,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Status badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderRadius: 12,
          background: status.bg,
          border: `1px solid ${status.color}40`,
        }}
      >
        <StatusIcon style={{ width: 20, height: 20, color: status.color }} />
        <span style={{ color: status.color, fontWeight: 700, fontSize: 15 }}>{status.label}</span>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <Kpi
          label={t('wb_kpi_total_weight', { ns: 'admin' })}
          value={`${formatNum(result.total_weight_lb, 1)} lb`}
          sub={result.mtow_lb ? `MTOW: ${formatNum(result.mtow_lb, 0)} lb` : '—'}
          color={overMtow ? '#ef4444' : null}
          darkMode={darkMode}
        />
        <Kpi
          label={t('wb_kpi_cg', { ns: 'admin' })}
          value={`${formatNum(result.cg_in, 2)} in`}
          sub={
            result.limits
              ? `${formatNum(result.limits.cg_min_in, 2)} – ${formatNum(result.limits.cg_max_in, 2)}`
              : '—'
          }
          color={
            result.envelope_status === 'cg_too_forward' || result.envelope_status === 'cg_too_aft'
              ? '#ef4444'
              : null
          }
          darkMode={darkMode}
        />
        <Kpi
          label={t('wb_kpi_fuel', { ns: 'admin' })}
          value={`${formatNum(result.fuel_used_gal, 1)} gal`}
          sub={`${formatNum(result.fuel_used_lb, 1)} lb`}
          darkMode={darkMode}
        />
      </div>

      {/* Breakdown */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            color: text,
          }}
        >
          <thead>
            <tr style={{ borderBottom: border }}>
              <th style={cellHead(sub, 'left')}>{t('wb_breakdown_item', { ns: 'admin' })}</th>
              <th style={cellHead(sub, 'right')}>
                {t('wb_breakdown_weight', { ns: 'admin' })} (lb)
              </th>
              <th style={cellHead(sub, 'right')}>
                {t('wb_breakdown_arm', { ns: 'admin' })} (in)
              </th>
              <th style={cellHead(sub, 'right')}>
                {t('wb_breakdown_moment', { ns: 'admin' })} (lb·in)
              </th>
            </tr>
          </thead>
          <tbody>
            {result.breakdown.map((b, i) => (
              <tr key={i} style={{ borderBottom: border }}>
                <td style={cell('left')}>{b.label}</td>
                <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                  {formatNum(b.weight_lb, 1)}
                </td>
                <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                  {formatNum(b.arm_in, 2)}
                </td>
                <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                  {formatNum(b.moment_lb_in, 1)}
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: `2px solid ${darkMode ? '#94a3b8' : '#0f172a'}` }}>
              <td style={cell('left')}>{t('wb_breakdown_total', { ns: 'admin' })}</td>
              <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                {formatNum(result.total_weight_lb, 1)}
              </td>
              <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                {formatNum(result.cg_in, 2)}
              </td>
              <td style={cell('right', { fontVariantNumeric: 'tabular-nums' })}>
                {formatNum(result.total_moment_lb_in, 1)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, color, darkMode }) => {
  const labelColor = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const valueColor = color || (darkMode ? 'white' : '#0f172a');
  const bg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)';
  const border = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.06)';

  return (
    <div
      style={{
        background: bg,
        border,
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <span style={{ fontSize: 11, color: labelColor, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      <span style={{ fontSize: 20, fontWeight: 800, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      <span style={{ fontSize: 11, color: labelColor, fontVariantNumeric: 'tabular-nums' }}>{sub}</span>
    </div>
  );
};

function formatNum(n, decimals = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(decimals);
}

function cellHead(sub, align) {
  return {
    textAlign: align,
    fontSize: 11,
    fontWeight: 600,
    color: sub,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    padding: '8px 6px',
  };
}

function cell(align, extra = {}) {
  return {
    textAlign: align,
    padding: '8px 6px',
    ...extra,
  };
}

export default WBResultPanel;
